import React, { useState } from 'react';
import { projects } from '../data/mock';
import { ExternalLink, ArrowUpRight } from 'lucide-react';
import { Card, Label, Tag } from './primitives';

const ProjectCard = ({ project, index }) => (
  <Card className="group relative h-full flex flex-col p-5 sm:p-6 transition-all duration-200 hover:shadow-hard-lift hover:-translate-x-[2px] hover:-translate-y-[2px]">
    {/* Number, as in the print portfolio */}
    <span className="absolute top-5 right-5 font-mono text-3xl font-bold text-flag/15 group-hover:text-flag/30 transition-colors select-none">
      {String(project.id).padStart(2, '0')}
    </span>

    <div className="relative pr-12 flex flex-col flex-1">
      {project.featured && <Label className="mb-2.5">Featured</Label>}

      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg sm:text-xl font-bold text-ink tracking-tight leading-snug">
          {project.title}
        </h3>
        {project.link !== '#' && (
          <ArrowUpRight
            size={18}
            className="text-subtle shrink-0 mt-1 group-hover:text-flag group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
          />
        )}
      </div>

      <p className="mt-3 text-sm text-ink-soft leading-relaxed">{project.description}</p>

      {project.highlight && (
        <p className="mt-3 text-[13px] text-ink font-medium">
          <span className="text-flag">✦ </span>
          {project.highlight}
        </p>
      )}

      <div className="mt-auto pt-4">
        <div className="flex flex-wrap gap-2">
          {project.tech.map((tech, i) => (
            <Tag key={tech} index={index + i}>
              {tech}
            </Tag>
          ))}
        </div>

        {project.link !== '#' && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-flag transition-colors"
          >
            <ExternalLink size={14} />
            View project
          </a>
        )}
      </div>
    </div>
  </Card>
);

const Projects = () => {
  const [showAll, setShowAll] = useState(false);
  const featured = projects.filter((p) => p.featured);
  const displayed = showAll ? projects : featured;

  return (
    <section
      id="projects"
      className="bg-paper border-y-[1.5px] border-ink py-16 sm:py-24 scroll-mt-20"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Label className="mb-3">Side builds</Label>
            <h2 className="text-2xl sm:text-4xl font-bold text-ink tracking-[-0.02em]">
              Things I built because nobody asked
            </h2>
          </div>
          <Label>
            {displayed.length} of {projects.length}
          </Label>
        </div>

        <div className="mt-9 sm:mt-10 grid md:grid-cols-2 md:auto-rows-fr gap-5 sm:gap-6">
          {displayed.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>

        {projects.length > featured.length && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-5 py-2.5 bg-cream text-ink text-sm font-semibold rounded-lg border-[1.5px] border-ink shadow-hard-sm hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
            >
              {showAll ? 'Show less' : `View all ${projects.length} projects`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
